import React, { useState } from 'react';
import '../assets/welcome.scss';
import checksign_icon from 'src/images/icon/checksign_icon.png';
import doc_icon from 'src/images/icon/doc_icon.png';
import market_icon from 'src/images/icon/market_icon.png'
import {HOME_LOCATION_PATH} from "src/constants/Steps";

interface WelcomePageProps {
    onGetStarted?: () => void;
}

const WelcomePage: React.FC<WelcomePageProps> = ({ onGetStarted }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isFranchiseDealer, setIsFranchiseDealer] = useState<boolean | null>(null);
    const [isReadyToOnboard, setIsReadyToOnboard] = useState<boolean | null>(null);

    const handleGetStartedClick = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setIsFranchiseDealer(null);
        setIsReadyToOnboard(null);
    };

    const handleSubmit = () => {
        if (onGetStarted) {
            onGetStarted();
        } else {
            window.location.href = HOME_LOCATION_PATH;
        }
        handleCloseModal();
    };

    const handleContactUs = () => {
        window.location.href = 'https://www.autos.amazon/contact-us';
        handleCloseModal();
    };

    const showSecondQuestion = isFranchiseDealer === true;
    const showSubmitButton = isFranchiseDealer === true && isReadyToOnboard === true;
    const showContactUsButton = isFranchiseDealer === false || (isFranchiseDealer === true && isReadyToOnboard === false);

    return (
        <>
                <div className="heading-section">
                    <h1>
                        <span className="welcome-heading">Welcome to </span>
                        <span className="started">Amazon Autos</span>
                    </h1>
                    <p className="welcome-text">We’re excited to help grow your business on the world’s biggest online retail site.</p>
                    <p className="welcome-text-disclaimer">At this time, Amazon Autos is focusing our expansion efforts on franchised dealerships in major metropolitan areas.</p>
                    <p className="welcome-text-disclaimer">We are not currently onboarding independent dealers but appreciate your interest in our program.</p>
                    <button className="get-started-button" onClick={handleGetStartedClick}>Get started</button>
                </div>

                {/* Modal */}
                {isModalOpen && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <h4>First, let's check your dealership's eligibility.</h4>
                            
                            <div className="modal-question">
                                <label className="form-label">Are you a franchise dealer?</label>
                                <div className="modal-radio-row">
                                    <label className="radiobutton-label">
                                        <input 
                                            type="radio" 
                                            name="franchiseDealer"
                                            checked={isFranchiseDealer === true}
                                            onChange={() => {
                                                setIsFranchiseDealer(true);
                                                setIsReadyToOnboard(null);
                                            }}
                                        />
                                        <span>Yes</span>
                                    </label>
                                    <label className="radiobutton-label">
                                        <input 
                                            type="radio" 
                                            name="franchiseDealer"
                                            checked={isFranchiseDealer === false}
                                            onChange={() => {
                                                setIsFranchiseDealer(false);
                                                setIsReadyToOnboard(null);
                                            }}
                                        />
                                        <span>No</span>
                                    </label>
                                </div>
                            </div>

                            {showSecondQuestion && (
                                <div className="modal-question">
                                    <label className="form-label">Are you ready to sign contracts and begin onboarding?</label>
                                    <div className="modal-radio-row">
                                        <label className="radiobutton-label">
                                            <input 
                                                type="radio" 
                                                name="readyToOnboard"
                                                checked={isReadyToOnboard === true}
                                                onChange={() => setIsReadyToOnboard(true)}
                                            />
                                            <span>Yes</span>
                                        </label>
                                        <label className="radiobutton-label">
                                            <input 
                                                type="radio" 
                                                name="readyToOnboard"
                                                checked={isReadyToOnboard === false}
                                                onChange={() => setIsReadyToOnboard(false)}
                                            />
                                            <span>No</span>
                                        </label>
                                    </div>
                                </div>
                            )}

                            <div className="modal-actions">
                                {showSubmitButton && (
                                    <button className="modal-submit-button" onClick={handleSubmit}>
                                        Get Started
                                    </button>
                                )}
                                {showContactUsButton && (
                                    <button className="modal-contact-button" onClick={handleContactUs}>
                                        Contact Us
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                
                <div className="content-section">
                    <div className="main-content-box">
                        <div className="info-section">
                            <p className="info-intro">Before you can register, we’ll need some information first:</p>
                            
                            <div className="step">
                                <div className="step-icon">
                                    <img className="checksign-icon" src={checksign_icon}/>
                                </div>
                                <div className="step-content">
                                    <div className="step-title">1. Dealership information (5-15 min)</div>
                                    <p className="step-description">You’ll fill out your dealership’s contact, business, and software information so we can get your account set up correctly on Amazon Autos.</p>
                                </div>
                            </div>
                            
                            <div className="step">
                                <div className="step-icon">
                                    <img className="doc-icon" src={doc_icon}/>
                                </div>
                                <div className="step-content">
                                    <div className="step-title">2. Review & sign contracts (5-10 min)</div>
                                    <p className="step-description">These contracts are for both Amazon and our third-party partners, and are required for Seller Central registration. <strong>If you don’t have the authority to sign legal documents for your dealership, please have an authorized person sign and submit these contracts.</strong></p>
                                </div>
                            </div>
                            
                            <div className="step">
                                <div className="step-icon">
                                    <img className="market-icon" src={market_icon}/>
                                </div>
                                <div className="step-content">
                                    <div className="step-title">3. Register with Amazon Seller Central (~2.5 hrs)</div>
                                    <p className="step-description">Once we verify your information, you’ll get access to register for Amazon Seller Central. Seller Central is where your dealership will manage your business and track your performance on Amazon Autos.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
        </>
    );
};

export default WelcomePage;
